import React, { memo, useCallback, useState } from 'react';
import { PayStateT } from 'routes/HumanResourcesModule/PayState/helpers/types';

import vizInfo, {
    DateVentilation,
    VizT,
    VizType,
} from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/helpers/hrVizInfos';
import { filterAndExtractDates } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import ToggleChoice from 'routes/HumanResourcesModule/components/HrDataGrid/ToggleChoice';
import { useGetAbsenceCostsEndpoint } from '../helpers/hooks/payStateData';
import ShowLastYearToggle from '../components/ShowLastYearToggle';
import AbsenceGrid from './components/AbsenceGrid';
import AbsenceGraph from './components/AbsenceGraph';

type Props = {
    payState: PayStateT;
};

const Absence = ({ payState }: Props): React.ReactElement => {
    const { id: payStateId, exercice } = payState;

    const [dateVentilation, setDateVentilation] = useState<DateVentilation>(
        DateVentilation.MONTHLY,
    );

    const [vizType, setVizType] = useState<VizT>(vizInfo.table);

    const [showLastYearColumns, setShowLastYearColumns] = useState(false);

    const isMonthly = dateVentilation === DateVentilation.MONTHLY;
    const isGraph = vizType.name === VizType.GRAPH;

    const { data: absenceResponse, isLoading: isDataGridResponseLoading } =
        useGetAbsenceCostsEndpoint(payStateId, isMonthly);
    const absenceData = absenceResponse?.data || [];
    const dateColumns = filterAndExtractDates(
        absenceData,
        isMonthly,
        payState.exercice,
    );

    const toggleLastYearColumns = useCallback(
        () => setShowLastYearColumns((current) => !current),
        [],
    );
    const cardHeaderActions = (
        <>
            <ShowLastYearToggle
                checked={showLastYearColumns}
                disabled={!isMonthly}
                onChange={toggleLastYearColumns}
                lastYear={exercice - 1}
            />
            <ToggleChoice<VizT>
                optionSelected={vizType}
                setOption={setVizType}
                option1={vizInfo.table}
                option2={vizInfo.graph}
            />
            <ToggleChoice<DateVentilation>
                optionSelected={dateVentilation}
                setOption={setDateVentilation}
                option1={DateVentilation.MONTHLY}
                option2={DateVentilation.YEARLY}
            />
        </>
    );

    return (
        <>
            {isGraph ? (
                <AbsenceGraph
                    absenceData={absenceData}
                    isLoading={isDataGridResponseLoading}
                    cardHeaderActions={cardHeaderActions}
                    exercice={exercice}
                    isMonthly={isMonthly}
                    dateColumns={dateColumns}
                    showLastYearColumns={showLastYearColumns}
                />
            ) : (
                <AbsenceGrid
                    absenceData={absenceData}
                    isLoading={isDataGridResponseLoading}
                    cardHeaderActions={cardHeaderActions}
                    exercice={exercice}
                    dateVentilation={dateVentilation}
                    dateColumns={dateColumns}
                    showLastYearColumns={showLastYearColumns}
                />
            )}
        </>
    );
};

export default memo(Absence);
